import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller, set } from "react-hook-form";
import { criarOrcamentoSchema } from "./schema/criar-orcamento.schema";
import { BudgetsContract } from "./contracts/criar-orcamento.contracts";
import { StutusBudgetEnum } from "./contracts/editar-orcamento.contract";
import { useEffect, useRef, useState } from "react";
import { limpaCamposPsFisica, limpaCamposPsJuridica } from "../../shared/utils/limpa-campo-form/limpa-campos.part";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { orcamentoService } from "./service/orcamento.service";
import { toast } from "react-toastify";
import { useLoader } from "../../shared/contexts/LoaderContext";

import {
    Box,
    Typography,
    useMediaQuery,
    useTheme,
    Divider,
    Grid,
} from "@mui/material";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";

import { PessoaJuridicaFormPart } from "../../shared/components/formulario/parts/PessoaJuridicaFormPart/pessoa-juridica.part";
import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import { SelectProfitMarginBudgetConfig } from "../../shared/components/formulario/input-select-profit-margin-budget-config/selectProfitMarginBudgetConfig";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { StepsEditor } from "./components/StepsEditor";
import { BudgetTotalFooterContainer } from "./components/BudgetTotalFooterContainer";
import InputData from "../../shared/components/formulario/input-data/inputData";

import { DiscountTypeEnum, PersonTypeEnum } from "../../shared/utils/enum/enum";
import { ICnpjData } from "../../shared/components/buscarCNPJ/buscar-cnpj";
import { InputSwitch } from "../../shared/components/formulario/input-switch/switch";
import Input from "../../shared/components/formulario/input/input";
import { mascaraTelefone } from "../../shared/utils/mascaras";
import { AiAnaliseOrcamentoModal } from "./components/AiAnaliseOrcamentoModal";
import { FloatingSaveButton, TextField } from "../../shared/components/ui";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import { InputRadio } from "../../shared/components/formulario/input-radio/InputRadio";
import { findFirstErrorMessage } from "../../shared/utils/form/find-first-error-message";

export default function CriarOrcamento() {

    const methods = useForm<BudgetsContract>({
        resolver: yupResolver(criarOrcamentoSchema),
        defaultValues: {
            isClient: true,
            isProfitMargin: false,
            personType: PersonTypeEnum.PF || null,
            fantasyName: "",
            corporateName: "",
            cnpj: "",
            cpf: "",
        },
    });

    const {
        handleSubmit,
        control,
        setValue,
        watch,
        formState: { errors },
    } = methods;

    // Exibe o primeiro erro de validação quando o submit é bloqueado pelo schema
    const onInvalidSubmit = (formErrors: typeof errors) => {
        console.error("Erros de validação do orçamento:", formErrors);
        const primeiraMensagem = findFirstErrorMessage(formErrors);
        toast.error(primeiraMensagem || "Existem campos inválidos no formulário.");
    };

    const navigate = useNavigate();
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));
    const [isDesconto, setIsDesconto] = useState(false);
    const [openAiModal, setOpenAiModal] = useState(false);
    const [budgetIdForAnalysis, setBudgetIdForAnalysis] = useState<string | null>(null);
    const [createdBudgetId, setCreatedBudgetId] = useState<string | null>(null);

    const isClient = watch("isClient");
    const personType = watch("personType");
    const isProfitMargin = watch("isProfitMargin");
    const { showLoader, hideLoader } = useLoader();

    const preencherCampos = (dados: ICnpjData) => {
        setValue("fantasyName", dados.fantasia || dados.razaoSocial || "");
        setValue("corporateName", dados.razaoSocial || "");
    };

    useEffect(() => {
        setValue("isDiscount", isDesconto);
        setIsDesconto(watch("isDiscount") || false);
    }, [isDesconto, setValue]);

    const personTypeAnteriorRef = useRef<PersonTypeEnum | null | undefined>(undefined);
    useEffect(() => {
        if (personTypeAnteriorRef.current && personTypeAnteriorRef.current !== personType) {
            if (personType === PersonTypeEnum.PJ) {
                limpaCamposPsFisica({ methods });
            } else if (personType === PersonTypeEnum.PF) {
                limpaCamposPsJuridica({ methods });
            }
        }
        personTypeAnteriorRef.current = personType;
    }, [personType, methods]);

    const { mutate } = useMutation<unknown, Error, BudgetsContract>({
        mutationFn: async (data) => {
            showLoader();
            return await orcamentoService.criar(data);
        },
        onSuccess: () => {
            hideLoader();
            toast.success("Orçamento criado com sucesso!");
            navigate("/orcamentos", { state: { reload: true } });
        },
        onError: (err: any) => {
            hideLoader();
            toast.error(err?.mensagem || "Erro ao criar orçamento.");
        },
    });

    // Salva o orçamento (criar ou editar), permanecendo na página
    const { mutate: mutateAndStay, isPending: salvandoFlutuante } = useMutation<unknown, Error, BudgetsContract>({
        mutationFn: async (data) => {
            showLoader();
            if (createdBudgetId) {
                // Se já foi criado, editar
                const editData = {
                    ...data,
                    status: StutusBudgetEnum.GENERATED,
                };
                return await orcamentoService.editar(createdBudgetId, editData as any);
            } else {
                // Primeira vez, criar
                return await orcamentoService.criar(data);
            }
        },
        onSuccess: (response: any) => {
            hideLoader();
            // Capturar o ID na primeira criação
            if (!createdBudgetId && response?.id) {
                setCreatedBudgetId(response.id);
            }
            toast.success("Orçamento salvo com sucesso!");
        },
        onError: (err: any) => {
            hideLoader();
            toast.error(err?.mensagem || "Erro ao salvar orçamento.");
        },
    });

    // Salva o orçamento e abre o modal de análise com IA
    const { mutate: mutateAndAnalyze, isPending: salvandoParaAnalise } = useMutation<any, Error, BudgetsContract>({
        mutationFn: async (data) => {
            showLoader();
            if (createdBudgetId) {
                // Se já foi criado, editar
                const editData = {
                    ...data,
                    status: StutusBudgetEnum.GENERATED,
                };
                return await orcamentoService.editar(createdBudgetId, editData as any);
            } else {
                // Primeira vez, criar
                return await orcamentoService.criar(data);
            }
        },
        onSuccess: (response: any) => {
            hideLoader();
            const id = createdBudgetId || response?.id || response;
            if (!createdBudgetId && response?.id) {
                setCreatedBudgetId(response.id);
            }
            toast.success("Orçamento salvo com sucesso!");
            setBudgetIdForAnalysis(id);
            setOpenAiModal(true);
        },
        onError: (err: any) => {
            hideLoader();
            toast.error(err?.mensagem || "Erro ao salvar orçamento.");
        },
    });

    return (
        <BaseLayout title="Criar Orçamento">
            <FerramanetasDetalhe
                mostraBotaoApagar={false}
                mostraBotaoNovo={false}
                mostraBotaoSalvarEVoltar={false}
                mostraBotaoVoltar
                mostrarBotaoAdicional2
                textoBotaoAdicional2="Analisar com IA"
                iconBotaoAdicional2="auto_awesome"
                aoClicarBotaoAdicional2={() => handleSubmit((data: BudgetsContract) => mutateAndAnalyze(data), onInvalidSubmit)()}
                aoClicarBotaoSalvar={handleSubmit((data: BudgetsContract) => mutate(data), onInvalidSubmit)}
                aoClicarBotaoVoltar={() => navigate("/orcamentos")}
            />
            <AiAnaliseOrcamentoModal open={openAiModal} onClose={() => { setOpenAiModal(false); setBudgetIdForAnalysis(null); }} methods={methods} budgetId={budgetIdForAnalysis || createdBudgetId} />
            <Box component="form" onSubmit={handleSubmit((data: BudgetsContract) => mutate(data), onInvalidSubmit)}>
                <Box display="flex" flexDirection="column" gap={4}>
                    {/* Seção: Dados do Cliente */}
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>Dados do Cliente</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <InputSwitch
                                    name="isClient"
                                    label="O cliente já possui cadastro no sistema?"
                                    methods={methods}
                                />
                            </Grid>
                            {isClient && (
                                <Grid item xs={12} md={12}>
                                    <Controller
                                        name="idClient"
                                        control={control}
                                        render={({ field }) => (
                                            <SelectCliente
                                                value={field.value ?? ""}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            {!isClient && (
                                <Grid item xs={12} md={12}>
                                    <InputRadio
                                        name="personType"
                                        label="Tipo de pessoa (Física ou Jurídica)"
                                        methods={methods}
                                        options={[
                                            { value: PersonTypeEnum.PJ, label: "Jurídica" },
                                            { value: PersonTypeEnum.PF, label: "Física" },
                                        ]}
                                        row
                                    />
                                    {personType === PersonTypeEnum.PJ && (
                                        <PessoaJuridicaFormPart methods={methods} preencherCampos={preencherCampos} />
                                    )}
                                    {personType === PersonTypeEnum.PF && (
                                        <PessoaFisicaFormPart methods={methods} mdDown={mdDown} />
                                    )}
                                </Grid>
                            )}
                            {/* Box de Contato */}
                            <Grid item xs={12}>
                                <Box
                                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40"
                                >
                                    <Typography
                                        variant="subtitle1"
                                        className="!text-slate-700 dark:!text-slate-200"
                                        sx={{ mb: 1, fontWeight: 500 }}
                                    >
                                        Contato
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Input
                                                name="email"
                                                label="E-mail"
                                                methods={methods}
                                                type="email"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputMascarado
                                                name="phone"
                                                methods={methods}
                                                label="Telefone"
                                                mask={mascaraTelefone}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Seção: Endereço */}
                    <Box>
                        <EnderecoFormPart methods={methods} mdDown={mdDown} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Seção: Dados do Orçamento */}
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>Dados do Orçamento</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <Input
                                    name="description"
                                    label="Nome do Orçamento"
                                    methods={methods}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputSwitch
                                    name="isProfitMargin"
                                    label="Deseja incluir margem de lucro no cálculo deste orçamento?"
                                    methods={methods}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InputData
                                    name="dateValidation"
                                    label="Validade do Orçamento"
                                    methods={methods}
                                    helperText={errors.dateValidation?.message}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Input
                                    name="percentageExtraCost"
                                    label="Custo Extra (%)"
                                    methods={methods}
                                    type="number"
                                />
                            </Grid>
                            {isProfitMargin && (
                                <Grid item xs={12} md={12}>
                                    <SelectProfitMarginBudgetConfig name="idProfitMargin" methods={methods} />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                        <Typography variant="h6">Etapas e Itens do Orçamento</Typography>
                        <StepsEditor methods={methods} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Seção: Desconto */}
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>Desconto</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <InputSwitch
                                    name="isDiscount"
                                    label="Deseja incluir desconto no cálculo do valor deste orçamento?"
                                    methods={methods}
                                />
                            </Grid>
                            {methods.watch("isDiscount") && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <InputRadio
                                            name="discountType"
                                            label="Forma de aplicação do desconto"
                                            methods={methods}
                                            options={[
                                                { value: "percentage", label: "Percentual sobre o valor (%)" },
                                                { value: "fixed", label: "Valor fixo do desconto (R$)" },
                                            ]}
                                            row
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="discountValue"
                                            control={control}
                                            render={({ field }) => (
                                                methods.watch("discountType") === "percentage" ? (
                                                    <TextField
                                                        {...field}
                                                        label="Valor do Desconto (%)"
                                                        type="number"
                                                        min={0}
                                                        step={0.01}
                                                        error={!!errors.discountValue}
                                                        helperText={errors.discountValue?.message}
                                                    />
                                                ) : (
                                                    <InputMascarado
                                                        name="discountValue"
                                                        methods={methods}
                                                        label="Valor do Desconto (R$)"
                                                        mask={mascaraMoedaReal}
                                                        helperText={errors.discountValue?.message}
                                                    />
                                                )
                                            )}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InputDescricao
                                name="observation"
                                methods={methods}
                                label="Observações"
                                placeholder="Escreva observações, escopo ou condições do orçamento..."
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <BudgetTotalFooterContainer methods={methods} />
            <FloatingSaveButton
                onClick={handleSubmit((data: BudgetsContract) => mutateAndStay(data), onInvalidSubmit)}
                loading={salvandoFlutuante}
                title="Salvar sem sair da página"
            />
        </BaseLayout>
    );
}